import {
    createStyles,
    LinearProgress,
    makeStyles,
    Tabs,
} from "@material-ui/core";
import * as React from "react";

import {
    MobileContent,
    MobileFooter,
    MobileHeader,
    MobilePageActionsProps,
} from ".";
import { Loading, RelodeTheme, useRelodeAppUI } from "../";
import { TabIndicator, TabItem } from "../tab";
import { MobilePageSectionProps } from "./mobilePageSection";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                marginTop: (props: MobilePageProps) =>
                    props.showTabs ? 0 : theme.relode.sizes.mobileHeaderHeight,
                marginBottom: (props: MobilePageProps) =>
                    !!props.footerActions
                        ? theme.relode.sizes.mobileFooterHeight
                        : 0,
                height: "100%",
                width: "100%",
            },
            section: {
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
            },
            tab: {
                padding: "0px 16px",
            },
            flexTabs: {
                borderBottom: "2px solid #F5F5F9",
            },
            busy: {
                height: 5,
                backgroundColor: theme.palette.secondary.dark,
            },
            notBusy: {
                height: 5,
                backgroundColor: "transparent",
            },
        }),
    { name: "MobilePage" }
);

interface MobilePageProps {
    defaultSection?: string;
    showTabs?: boolean;
    actions?: React.ReactElement<MobilePageActionsProps>;
    footerActions?: (selectedSection: string) => React.ReactNode;
    children: React.ReactNode;
    isBusy?: boolean;
    busyType?: "bar" | "overlay" | "both";
    pageTitle?: string;
    onTabClick?: (value: string) => void;
}

export const MobilePage = (props: MobilePageProps) => {
    const classes = useStyles(props);
    const [selectedSection, setSelectedSection] = React.useState(
        props.defaultSection
    );
    const {
        actions,
        children,
        showTabs = false,
        busyType = "both",
        isBusy = false,
        pageTitle,
        onTabClick,
    } = props;
    const { ui } = useRelodeAppUI();

    React.useEffect(() => {
        if (props.pageTitle) {
            ui.actions.setPageTitle(pageTitle);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageTitle]);

    const handleSelectedSection = (
        event: React.ChangeEvent<{}>,
        value: any
    ) => {
        if (onTabClick) {
            onTabClick(value);
        }
        setSelectedSection(value);
        window.scrollTo(0, 0);
    };

    const determineMobileIndicator = (
        changed: boolean,
        errors: boolean,
        info: boolean
    ): TabIndicator => {
        //helper function to set the indicator for mobile pages
        if (errors) {
            return "error";
        } else if (changed) {
            return "change";
        } else if (info) {
            return "info";
        } else {
            return null;
        }
    };

    const renderTabs = () =>
        React.Children.map(
            props.children,
            (
                child: React.ReactElement<MobilePageSectionProps>,
                index: number
            ) => {
                if (child) {
                    const { label, id, isDirty, count, errors, info } =
                        child.props;
                    return (
                        <TabItem
                            id={"SectionTab-" + id}
                            key={index}
                            label={label}
                            value={id}
                            count={count}
                            tabIndicator={determineMobileIndicator(
                                isDirty,
                                errors,
                                info
                            )}
                            classes={{ tab: classes.tab }}
                        ></TabItem>
                    );
                }
            }
        );

    const renderSections = () =>
        React.Children.map(
            children,
            (child: React.ReactElement<MobilePageSectionProps>) => {
                if (child) {
                    const { id } = child.props;
                    return selectedSection === id ? child : null;
                }
            }
        );

    return (
        <div className={classes.root}>
            <MobileHeader
                showTabs={showTabs}
                showStatusLabel={!!actions && !!actions.props.statusLabel}
            >
                <>
                    {isBusy ? (
                        <LinearProgress
                            color="primary"
                            classes={{ root: classes.busy }}
                        />
                    ) : (
                        <div className={classes.notBusy} />
                    )}

                    {!!actions ? <>{actions}</> : null}
                    {showTabs ? (
                        <Tabs
                            value={selectedSection}
                            onChange={(e, v) => handleSelectedSection(e, v)}
                            scrollButtons="auto"
                            variant="scrollable"
                            classes={{ flexContainer: classes.flexTabs }}
                        >
                            {renderTabs()}
                        </Tabs>
                    ) : null}
                </>
            </MobileHeader>
            <MobileContent
                hasTabs={showTabs}
                hasFooter={!!props.footerActions}
                hasStatusLabel={!!actions && !!actions.props.statusLabel}
            >
                <Loading
                    isLoading={isBusy && busyType !== "bar"}
                    variant="blur"
                    showIndicator={busyType !== "bar"}
                    fillSpace
                >
                    <div className={classes.section}>{renderSections()}</div>
                </Loading>
            </MobileContent>
            <MobileFooter>
                {!!props.footerActions
                    ? props.footerActions(selectedSection)
                    : null}
            </MobileFooter>
        </div>
    );
};
